import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Sidebar } from 'primereact/sidebar';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { useAuth } from '../contexts/AuthContext';
import { getMenuItemsForUser, MenuItem } from '../config/navigation';
import { Menu } from 'primereact/menu';
import { Avatar } from 'primereact/avatar';
import { Badge } from 'primereact/badge';
import { projectFoldersAPI, usersAPI, announcementsAPI, taskNotificationsAPI } from '../services/api';
import { playSound, showDesktopNotification, isDesktopNotificationsEnabled } from '../utils/notificationSound';
import { ProjectFolder, Announcement } from '../types';
import ITTicketModal from './ITTicketModal';
import AnnouncementModal from './AnnouncementModal';
import AnnouncementPopup from './AnnouncementPopup';
import CustomAlert, { showCustomAlert } from './CustomAlert';
import './Layout.css';

const Layout: React.FC = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);

  // Check for active announcement on mount and when user changes
  useEffect(() => {
    const checkActiveAnnouncement = async () => {
      try {
        const response = await announcementsAPI.getActive();
        // Don't show announcement if:
        // 1. No active announcement
        // 2. User already acknowledged it
        // 3. User is the creator (admin who created it)
        if (response.announcement && 
            !response.announcement.acknowledged &&
            response.announcement.createdBy._id !== user?._id) {
          setActiveAnnouncement(response.announcement);
        } else {
          setActiveAnnouncement(null);
        }
      } catch (err) {
        console.error('Error checking active announcement:', err);
      }
    };

    if (user) {
      checkActiveAnnouncement();
    }
  }, [user]);
  
  // Učitaj expanded menus iz localStorage pri inicijalizaciji
  const loadExpandedMenusFromStorage = (): Set<string> => {
    try {
      const stored = localStorage.getItem('menuExpandedSections');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return new Set(parsed);
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading expanded menus from localStorage:', err);
    }
    return new Set();
  };

  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(loadExpandedMenusFromStorage);
  const [folders, setFolders] = useState<ProjectFolder[]>([]);
  const [avatarDialogVisible, setAvatarDialogVisible] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const unreadCountRequestInFlightRef = useRef(false);
  const lastUnreadRefreshAtRef = useRef(0);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [itTicketModalVisible, setItTicketModalVisible] = useState(false);
  const [announcementModalVisible, setAnnouncementModalVisible] = useState(false);
  const [activeAnnouncement, setActiveAnnouncement] = useState<Announcement | null>(null);

  useEffect(() => {
    if (user) {
      loadFolders();
    }
  }, [user]);

  useEffect(() => {
    let mounted = true;
    let intervalId: any;

    const loadUnread = async () => {
      if (!user) return;
      if (unreadCountRequestInFlightRef.current) return;
      unreadCountRequestInFlightRef.current = true;
      try {
        const res = await taskNotificationsAPI.getUnreadCount();
        const count = res.unreadCount || 0;
        if (mounted) {
          setUnreadNotificationsCount((prev) => {
            if (count > prev && prev !== 0) {
              // Zvuk će se automatski reprodukovati u showDesktopNotification
              showDesktopNotification('Nova notifikacija', {
                body: 'Imate nove notifikacije zadataka.',
                icon: '/favicon.ico',
              });
              // Reprodukuj i običan zvuk za notifikacije (ako desktop notifikacije nisu omogućene)
              if (!isDesktopNotificationsEnabled()) {
                playSound('notification');
              }
            }
            return count;
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error loading unread notifications count:', err);
      } finally {
        unreadCountRequestInFlightRef.current = false;
      }
    };

    if (user) {
      void loadUnread();
      // Poll as a fallback (covers multi-tab/server-side changes)
      intervalId = setInterval(loadUnread, 30000);
    }

    const refreshIfNeeded = () => {
      const now = Date.now();
      // Throttle bursts from focus/custom events.
      if (now - lastUnreadRefreshAtRef.current < 3000) return;
      lastUnreadRefreshAtRef.current = now;
      void loadUnread();
    };
    const onFocus = () => refreshIfNeeded();
    const onUpdated = () => refreshIfNeeded();
    window.addEventListener('focus', onFocus);
    window.addEventListener('task-notifications-updated', onUpdated as any);

    return () => {
      mounted = false;
      if (intervalId) clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('task-notifications-updated', onUpdated as any);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);

  const loadFolders = async () => {
    try {
      const data = await projectFoldersAPI.getAll();
      setFolders(data.sort((a, b) => a.order - b.order));
    } catch (err) {
      console.error('Error loading folders:', err);
    }
  };

  if (!user) {
    return null;
  }

  const baseMenuItems = getMenuItemsForUser(user.role, user.subRole);
  
  // Dinamički dodaj foldere u Task Management sekciju
  const menuItems = baseMenuItems.map(item => {
    if (item.label === 'Task Management' && item.items) {
      // Ukloni hardkodovane "IT Projekti" i "Grafički Dizajn"
      const filteredItems = item.items.filter(
        subItem => subItem.path !== '/projects/it' && subItem.path !== '/projects/design'
      );
      
      // Dodaj dinamičke foldere
      const folderItems: MenuItem[] = folders.map(folder => ({
        label: folder.name,
        icon: folder.icon,
        path: `/projects/folder/${folder.slug}`
      }));
      
      return {
        ...item,
        items: [
          filteredItems[0], // "Svi projekti"
          ...folderItems,   // Dinamički folderi
          ...filteredItems.slice(1) // Ostali itemi (Upravljaj folderima, Moji IT tiketi)
        ]
      };
    }
    return item;
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const toggleMenu = (label: string) => {
    const newExpanded = new Set(expandedMenus);
    if (newExpanded.has(label)) {
      newExpanded.delete(label);
    } else {
      newExpanded.add(label);
    }
    setExpandedMenus(newExpanded);
    
    // Sačuvaj u localStorage
    try {
      localStorage.setItem('menuExpandedSections', JSON.stringify(Array.from(newExpanded)));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error saving expanded menus to localStorage:', err);
    }
  };

  const getRoleDisplayName = (role: string) => {
    const roleMap: Record<string, string> = {
      admin: 'Administrator',
      logistika: 'Logistika',
      magacin: 'Magacin',
      magacin_kontrola: 'Magacin – Kontrola Ulaza',
      visitor: 'Posetilac',
      direktor: 'Direktor',
      lager: 'Lager',
      odrzavanje: 'Održavanje',
      attendance_manager: 'Menadžer prisustva',
      finansije: 'Finansije',
      finansijski_gotovi: 'Finansijski gotovi',
    };
    return roleMap[role] || role;
  };

  const renderMenuItem = (item: any, level: number = 0) => {
    const hasChildren = item.items && item.items.length > 0;
    const isExpanded = expandedMenus.has(item.label);
    const active = isActive(item.path);

    if (hasChildren) {
      return (
        <div key={item.label} className={`menu-section ${level > 0 ? 'menu-subsection' : ''}`}>
          <div
            className={`menu-section-header ${isExpanded ? 'expanded' : ''}`}
            onClick={() => toggleMenu(item.label)}
            style={{ paddingLeft: `${level * 1.5}rem` }}
          >
            <i className={`menu-section-icon ${item.icon}`}></i>
            <span className="menu-section-label">{item.label}</span>
            <i className={`pi pi-chevron-${isExpanded ? 'down' : 'right'} menu-chevron`}></i>
          </div>
          <div className={`menu-section-items ${isExpanded ? 'expanded' : ''}`}>
            {item.items.map((subItem: any) => renderMenuItem(subItem, level + 1))}
          </div>
        </div>
      );
    }

    return (
      <div
        key={item.label}
        className={`menu-item ${active ? 'menu-item-active' : ''}`}
        onClick={() => {
          if (item.path) {
            navigate(item.path);
            setSidebarVisible(false);
          }
        }}
        style={{ paddingLeft: `${level * 1.5 + 1}rem` }}
      >
        <i className={`menu-item-icon ${item.icon}`}></i>
        <span className="menu-item-label">{item.label}</span>
      </div>
    );
  };

  return (
    <div className="layout-wrapper">
      {/* Desktop Sidebar */}
      <div className="desktop-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="pi pi-building" style={{ fontSize: '1.5rem', color: '#6b7280' }}></i>
            <div>
              <h2 className="sidebar-title">Thermowool ERP</h2>
              <p className="sidebar-subtitle">THWL ERP SYSTEM</p>
            </div>
          </div>
        </div>

        <div className="sidebar-menu">
          {menuItems.map((item) => renderMenuItem(item))}
        </div>

        <div className="sidebar-footer">
          <div className="user-info" style={{ cursor: 'pointer' }} onClick={() => setAvatarDialogVisible(true)}>
            <Avatar
              image={user.avatarUrl}
              label={`${user.firstName[0]}${user.lastName[0]}`}
              shape="circle"
              size="normal"
              style={{ backgroundColor: '#f3f4f6', color: '#6b7280' }}
            />
            <div className="user-details">
              <div className="user-name">{user.firstName} {user.lastName}</div>
              <div className="user-role">{getRoleDisplayName(user.role)}</div>
            </div>
          </div>
          <Button
            label="Odjavi se"
            icon="pi pi-sign-out"
            className="logout-button"
            onClick={handleLogout}
            severity="danger"
            outlined
          />
        </div>
      </div>

      {/* Main Content Area */}
      <div className="layout-main">
        {/* Top Toolbar */}
        <div className="layout-toolbar">
          <div className="toolbar-left">
            <Button
              icon="pi pi-bars"
              className="mobile-menu-btn"
              onClick={toggleSidebar}
              text
              rounded
            />
            <h1 className="page-title">
              {location.pathname === '/dashboard' ? 'Dashboard' : 
               (() => {
                 const parts = location.pathname.split('/');
                 const lastPart = parts[parts.length - 1];
                 if (!lastPart) return 'Dashboard';
                 return lastPart.charAt(0).toUpperCase() + lastPart.slice(1);
               })()}
            </h1>
          </div>
          <div className="toolbar-right">
            <div className="toolbar-icon-wrapper">
              <Button 
                icon="pi pi-bell" 
                className="toolbar-icon" 
                text 
                rounded 
                onClick={() => navigate('/notifications')}
                tooltip="Notifikacije"
              />
              {unreadNotificationsCount > 0 && (
                <Badge value={unreadNotificationsCount} severity="danger" className="toolbar-badge" />
              )}
            </div>
            {(user?.role === 'admin' || user?.role === 'direktor') && (
              <Button 
                icon="pi pi-volume-up" 
                className="toolbar-icon" 
                text 
                rounded 
                onClick={() => setAnnouncementModalVisible(true)}
                tooltip="Novo obaveštenje"
              />
            )}
            <Button 
              icon="pi pi-cog" 
              className="toolbar-icon" 
              text 
              rounded 
              onClick={() => setItTicketModalVisible(true)}
              tooltip="Prijavi IT problem"
            />
            <Button
              icon="pi pi-sign-out"
              className="logout-button-toolbar"
              onClick={handleLogout}
              severity="danger"
              text
              rounded
              tooltip="Odjavi se"
            />
          </div>
        </div>

        {/* Page Content */}
        <div className="layout-content">
          <Outlet />
        </div>
      </div>

      {/* Mobile Sidebar */}
      <Sidebar
        visible={sidebarVisible}
        onHide={() => setSidebarVisible(false)}
        position="left"
        className="mobile-sidebar"
        showCloseIcon={false}
        modal
      >
        <div className="mobile-sidebar-content">
          <div className="mobile-sidebar-header">
            <h2>Thermowool ERP</h2>
            <Button
              icon="pi pi-times"
              onClick={() => setSidebarVisible(false)}
              text
              rounded
              aria-label="Zatvori meni"
            />
          </div>
          <div className="mobile-sidebar-menu">
            {menuItems.map((item) => renderMenuItem(item))}
          </div>
          <div className="mobile-sidebar-footer">
            <div className="user-info-mobile">
              <Avatar
                image={user.avatarUrl}
                label={`${user.firstName[0]}${user.lastName[0]}`}
                shape="circle"
                size="normal"
                style={{ backgroundColor: '#f3f4f6', color: '#6b7280' }}
              />
              <div className="user-details">
                <div className="user-name">{user.firstName} {user.lastName}</div>
                <div className="user-role">{getRoleDisplayName(user.role)}</div>
              </div>
            </div>
            <Button
              label="Odjavi se"
              icon="pi pi-sign-out"
              className="logout-button"
              onClick={handleLogout}
              severity="danger"
              outlined
            />
          </div>
        </div>
      </Sidebar>

      {/* Avatar Upload Dialog */}
      <Dialog
        header="Promeni profilnu sliku"
        visible={avatarDialogVisible}
        style={{ width: '400px' }}
        onHide={() => {
          setAvatarDialogVisible(false);
          setAvatarPreview(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }}
        footer={
          <div>
            <Button 
              label="Otkaži" 
              icon="pi pi-times" 
              onClick={() => {
                setAvatarDialogVisible(false);
                setAvatarPreview(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }} 
              className="p-button-text" 
            />
            <Button 
              label="Sačuvaj" 
              icon="pi pi-check" 
              onClick={async () => {
                if (fileInputRef.current?.files && fileInputRef.current.files.length > 0) {
                  try {
                    const file = fileInputRef.current.files[0];
                    const reader = new FileReader();
                    reader.onloadend = async () => {
                      try {
                        const base64String = reader.result as string;
                        const result = await usersAPI.updateAvatar(base64String);
                        setAvatarDialogVisible(false);
                        setAvatarPreview(null);
                        // Refresh user data
                        if (refreshUser) {
                          await refreshUser();
                        } else {
                          window.location.reload();
                        }
                      } catch (err) {
                        console.error('Error uploading avatar:', err);
                        showCustomAlert.error('Greška pri upload-u avatar-a');
                      }
                    };
                    reader.readAsDataURL(file);
                  } catch (err) {
                    console.error('Error reading file:', err);
                    showCustomAlert.error('Greška pri čitanju fajla');
                  }
                }
              }} 
            />
          </div>
        }
      >
        <div style={{ padding: '1rem 0', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {avatarPreview ? (
              <img 
                src={avatarPreview} 
                alt="Preview" 
                style={{ 
                  width: '150px', 
                  height: '150px', 
                  borderRadius: '50%', 
                  objectFit: 'cover',
                  border: '2px solid #e5e7eb'
                }} 
              />
            ) : (
              <Avatar
                image={user.avatarUrl}
                label={`${user.firstName[0]}${user.lastName[0]}`}
                shape="circle"
                size="xlarge"
                style={{ backgroundColor: '#f3f4f6', color: '#6b7280' }}
              />
            )}
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  if (file.size > 200 * 1024) {
                    showCustomAlert.warning('Slika mora biti manja od 200KB');
                    e.target.value = '';
                    return;
                  }
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setAvatarPreview(reader.result as string);
                  };
                  reader.readAsDataURL(file);
                }
              }}
            />
            <Button
              label="Izaberi sliku"
              icon="pi pi-upload"
              onClick={() => fileInputRef.current?.click()}
              style={{ width: '100%' }}
            />
            <small style={{ display: 'block', marginTop: '0.5rem', color: '#6b7280', textAlign: 'center' }}>
              Maksimalna veličina: 200KB. Dozvoljeni formati: PNG, JPG, WEBP
            </small>
          </div>
          </div>
        </Dialog>

      {/* IT Ticket Modal */}
      <ITTicketModal
        visible={itTicketModalVisible}
        onHide={() => setItTicketModalVisible(false)}
        onSuccess={() => {
          // Refresh or navigate if needed
        }}
      />

      {/* Announcement Modal (for creating) */}
      {(user?.role === 'admin' || user?.role === 'direktor') && (
        <AnnouncementModal
          visible={announcementModalVisible}
          onHide={() => setAnnouncementModalVisible(false)}
          onSuccess={async () => {
            // Refresh active announcement after creating new one
            // Admin won't see it since they created it
            try {
              const response = await announcementsAPI.getActive();
              if (response.announcement && 
                  !response.announcement.acknowledged &&
                  response.announcement.createdBy._id !== user?._id) {
                setActiveAnnouncement(response.announcement);
              } else {
                setActiveAnnouncement(null);
              }
            } catch (err) {
              console.error('Error checking active announcement:', err);
            }
          }}
        />
      )}

      {/* Announcement Popup (full-screen for all users) */}
      {activeAnnouncement && activeAnnouncement.createdBy._id !== user?._id && (
        <AnnouncementPopup
          announcement={activeAnnouncement}
          onAcknowledge={async () => {
            // After acknowledging, refresh to check if announcement is still active
            // It should be null now since user acknowledged it
            try {
              const response = await announcementsAPI.getActive();
              if (response.announcement && 
                  !response.announcement.acknowledged &&
                  response.announcement.createdBy._id !== user?._id) {
                setActiveAnnouncement(response.announcement);
              } else {
                setActiveAnnouncement(null);
              }
            } catch (err) {
              console.error('Error checking active announcement:', err);
              setActiveAnnouncement(null);
            }
          }}
        />
      )}

      {/* Custom Alert Dialog */}
      <CustomAlert />
    </div>
  );
};

export default Layout;
